import React, {useState, useEffect} from 'react';
import {Platform} from 'react-native';
import messaging from './../../utils/firebase/messaging.web';
import useSessionStorageState from 'use-session-storage-state';
import {useContextState} from '../../context/user';

const PERSISTENCE_KEY = 'NAVIGATION_STATE_V1';

const BROADCAST_CHANNEL_PUSH = 'sw-messages';
const BROADCAST_CHANNEL_EVENT = 'message';

export const useMainProperties = () => {
  const {
    isAnonymous,
    isClient,
    isSubClient,
    isSalesMan,
    addNotification,
    setGoToMessages,
    isOpenedWebSession,
    signinWebSession,
    registerDevice,
    isDefaultSubClient,
    isEnchancedSubClient,
    profile,
  } = useContextState();

  const [hasMessageArrived, setHasMessageArrived] = useState(false);
  const [isReady, setIsReady] = React.useState(false);
  const [initialState, setInitialState] = useState();
  const [stateNavigation, saveStateNavigation] = useSessionStorageState<
    any | unknown
  >(PERSISTENCE_KEY);

  const saveTokenToBackend = async (
    email: string,
    token: string,
    device: string,
  ) => {
    await registerDevice(email, token, device);
  };

  async function requestUserPermission(email: string) {
    try {
      const authStatus = await messaging().requestPermission();
      const enabled =
        authStatus === messaging.AuthorizationStatus.AUTHORIZED ||
        authStatus === messaging.AuthorizationStatus.PROVISIONAL;

      if (enabled) {
        try {
          const firebaseToken = await messaging().getToken();
          await saveTokenToBackend(email, firebaseToken, Platform.OS);
        } catch {}
      }
    } catch {}

    return false;
  }

  const handleRemoteMessage = (
    notification: {[key: string]: string} | undefined,
  ) => {
    if (notification) {
      const {title, body} = notification;
      if (title != null && body != null) {
        addNotification(title, body);
      }
    }
  };

  useEffect(() => {
    const unsubscribeOnMessage = messaging().onMessage(async remoteMessage => {
      handleRemoteMessage(remoteMessage.notification);
    });
    const channel = new BroadcastChannel(BROADCAST_CHANNEL_PUSH);
    channel.addEventListener(BROADCAST_CHANNEL_EVENT, event => {
      handleRemoteMessage(event.data);
    });

    if (isOpenedWebSession()) {
      signinWebSession();
    }

    processInitialNotification();

    return unsubscribeOnMessage;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const restoreState = async () => {
      try {
        if (stateNavigation !== undefined) {
          setInitialState(stateNavigation);
        }
      } finally {
        setIsReady(true);
      }
    };

    if (!isReady) {
      restoreState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady]);

  const processInitialNotification = async () => {
    const initialNotification = await messaging().getInitialNotification();
    if (initialNotification) {
      setHasMessageArrived(true);
    }
  };

  useEffect(() => {
    if (hasMessageArrived) {
      setHasMessageArrived(false);
      setGoToMessages(true);
    }
  }, [hasMessageArrived, isAnonymous, setGoToMessages]);

  useEffect(() => {
    if (profile && profile.email) {
      try {
        requestUserPermission(profile.email);
      } catch {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  const groupOptions = {
    screenOptions: {headerTransparent: true, headerTitle: ''},
  };

  const onStateChange = (state: any) => saveStateNavigation(state);

  const navigationContainerProps = {
    initialState,
    onStateChange,
  };

  const nativeBaseProviderProps = {};

  return {
    isAnonymous,
    isClient,
    isSubClient,
    isSalesMan,
    groupOptions,
    initialState,
    onStateChange,
    navigationContainerProps,
    nativeBaseProviderProps,
    isDefaultSubClient,
    isEnchancedSubClient,
  };
};
